import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { termsOfUseSV, termsOfUseEN } from '@/data/legal/terms-of-use';
import { ChevronDown, ChevronUp } from 'lucide-react';
import LanguageFlag from '@/components/LanguageFlag';

interface TermsOfUseProps {
  language?: 'sv' | 'en';
  onLanguageChange?: (lang: 'sv' | 'en') => void;
  showLanguageToggle?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  showExpandToggle?: boolean;
  acceptButton?: {
    show: boolean;
    onAccept: () => void;
    disabled?: boolean;
  };
}

export default function TermsOfUse({
  language = 'sv',
  onLanguageChange,
  showLanguageToggle = true,
  expanded = true,
  onToggleExpand,
  showExpandToggle = false,
  acceptButton,
}: TermsOfUseProps) {
  const [currentLang, setCurrentLang] = useState<'sv' | 'en'>(language);

  const handleLanguageChange = (lang: 'sv' | 'en') => {
    setCurrentLang(lang);
    onLanguageChange?.(lang);
  };

  const termsContent = currentLang === 'sv' ? termsOfUseSV : termsOfUseEN;
  const title = currentLang === 'sv' ? 'Användarvillkor' : 'Terms of Use';

  return (
    <div className="space-y-4">
      {/* Header with language toggle */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold">{title}</h2>
        {showLanguageToggle && (
          <div className="flex gap-2">
            <button
              onClick={() => handleLanguageChange('sv')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                currentLang === 'sv'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
              title="Svenska"
            >
              <span className="inline-flex items-center gap-1.5">
                <LanguageFlag lang="sv" />
                Svenska
              </span>
            </button>
            <button
              onClick={() => handleLanguageChange('en')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                currentLang === 'en'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
              title="English"
            >
              <span className="inline-flex items-center gap-1.5">
                <LanguageFlag lang="en" />
                English
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Expand/Collapse toggle for onboarding */}
      {showExpandToggle && (
        <button
          onClick={onToggleExpand}
          className="w-full flex items-center justify-between p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
        >
          <span className="font-medium">
            {expanded 
              ? (currentLang === 'sv' ? 'Dölj villkor' : 'Hide terms')
              : (currentLang === 'sv' ? 'Visa villkor' : 'Show terms')}
          </span>
          {expanded ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </button>
      )}

      {/* Terms content */}
      {expanded && (
        <div className="prose prose-sm max-w-none dark:prose-invert bg-card border border-border rounded-lg p-6 max-h-[60vh] overflow-y-auto">
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <h1 className="text-2xl font-bold mb-4 text-foreground">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-xl font-semibold mt-6 mb-3 text-foreground">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-lg font-medium mt-4 mb-2 text-foreground">{children}</h3>
              ),
              p: ({ children }) => (
                <p className="mb-3 text-muted-foreground leading-relaxed">{children}</p>
              ),
              ul: ({ children }) => (
                <ul className="list-disc list-inside mb-3 space-y-1 text-muted-foreground">{children}</ul>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-foreground">{children}</strong>
              ),
              hr: () => <hr className="my-6 border-border" />,
            }}
          >
            {termsContent}
          </ReactMarkdown>
        </div>
      )}

      {/* Accept button for onboarding */}
      {acceptButton?.show && (
        <button
          onClick={acceptButton.onAccept}
          disabled={acceptButton.disabled}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
            acceptButton.disabled
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-primary text-primary-foreground hover:bg-primary/90'
          }`}
        >
          {currentLang === 'sv' 
            ? 'Jag godkänner användarvillkoren'
            : 'I accept the Terms of Use'}
        </button>
      )}
    </div>
  );
}
